import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const UpsetLikePlot = ({ data }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1600, height: 700 });

  // Handle responsive resizing
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Set width to container width with some padding, maintain aspect ratio
        const newWidth = Math.max(containerWidth - 40, 800); // Minimum 800px
        const newHeight = Math.max(newWidth * 0.4375, 500); // Maintain ~16:7 aspect ratio, minimum 500px
        setDimensions({ width: newWidth, height: newHeight });
      }
    };

    // Initial size calculation
    handleResize();

    // Add resize listener
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!data || !svgRef.current) return;

    const { width, height } = dimensions;
    const margin = { top: 40, left: 180, right: 40, bottom: 40 };

    const barChartHeight = height * 0.36; // Proportional to height
    const matrixHeight = height * 0.43; // Proportional to height
    const totalWidth = width - margin.left - margin.right;
    const leftBarWidth = Math.min(120, totalWidth * 0.1); // Proportional, max 120

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", "100%").attr("height", height).attr("viewBox", `0 0 ${width} ${height}`);

    const g = svg.append("g").attr(
      "transform",
      `translate(${margin.left}, ${margin.top})`
    );

    const intersections = [...data.intersections].sort((a, b) => b.size - a.size);
    
    // Sort sets by size ascending (smallest to largest)
    const sets = data.sets
      .map((d) => ({ name: d.name, size: d.size }))
      .sort((a, b) => a.size - b.size);

    // Consistent styling - responsive font size
    const dotRadius = Math.max(6, width * 0.005);
    const fontSize = `${Math.max(0.75, width * 0.0006)}rem`;
    const fontWeight = "normal";

    // Calculate maximum label width dynamically
    const tempText = svg.append("text")
      .attr("font-size", fontSize)
      .attr("font-weight", fontWeight)
      .style("visibility", "hidden");

    let maxLabelWidth = 0;
    sets.forEach((set) => {
      tempText.text(set.name);
      const bbox = tempText.node().getBBox();
      maxLabelWidth = Math.max(maxLabelWidth, bbox.width);
    });
    tempText.remove();

    // Add padding to label space
    const labelSpace = maxLabelWidth + 5;

    const x = d3
      .scaleBand()
      .domain(d3.range(intersections.length))
      .range([labelSpace, totalWidth])
      .padding(0.3);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(intersections, (d) => d.size)])
      .range([barChartHeight, 0])
      .nice();

    // Colors from screenshot
    const colors = {
      bar: "#4472c4",
      barBorder: "#2e5c8a",
      dot: "#4472c4",
      bgDot: "#d9e2f3",
      line: "#4472c4",
      leftBar: "#4472c4",
      leftBarBorder: "#2e5c8a",
    };

    // ---------- TOP BAR CHART ----------
    const barGroup = g.append("g");

    // Y-axis positioned at the start of the bars (next to the graph)
    const yAxis = d3.axisLeft(y).ticks(5).tickFormat(d3.format(",d"));
    
    barGroup
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${labelSpace}, 0)`)
      .call(yAxis)
      .selectAll("text")
      .attr("font-size", fontSize)
      .attr("font-weight", fontWeight);

    barGroup
      .selectAll(".bar")
      .data(intersections)
      .enter()
      .append("rect")
      .attr("x", (_, i) => x(i))
      .attr("y", (d) => y(d.size))
      .attr("width", x.bandwidth())
      .attr("height", (d) => barChartHeight - y(d.size))
      .attr("fill", colors.bar);

    barGroup
      .selectAll(".label")
      .data(intersections)
      .enter()
      .append("text")
      .attr("x", (_, i) => x(i) + x.bandwidth() / 2)
      .attr("y", (d) => y(d.size) - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", fontSize)
      .attr("font-weight", fontWeight)
      .attr("fill", "#000")
      .text((d) => d.size.toLocaleString());

    // ---------- MATRIX SECTION ----------
    const matrixGroup = g.append("g").attr(
      "transform",
      `translate(0, ${barChartHeight + 60})`
    );

    const rowScale = d3
      .scaleBand()
      .domain(sets.map(s => s.name))
      .range([0, matrixHeight])
      .padding(0.3);

    // Background faded dot grid
    sets.forEach((set) => {
      intersections.forEach((_, col) => {
        matrixGroup
          .append("circle")
          .attr("cx", x(col) + x.bandwidth() / 2)
          .attr("cy", rowScale(set.name) + rowScale.bandwidth() / 2)
          .attr("r", dotRadius)
          .attr("fill", colors.bgDot);
      });
    });

    // Active dots + lines
    intersections.forEach((intr, col) => {
      const xPos = x(col) + x.bandwidth() / 2;

      const activeRows = intr.sets.map((s) => ({
        yPos: rowScale(s) + rowScale.bandwidth() / 2,
      }));

      if (activeRows.length > 1) {
        const lineData = activeRows.map((d) => [xPos, d.yPos]);

        matrixGroup
          .append("path")
          .attr("d", d3.line()(lineData))
          .attr("stroke", colors.line)
          .attr("stroke-width", 2.5)
          .attr("fill", "none");
      }

      activeRows.forEach((d) => {
        matrixGroup
          .append("circle")
          .attr("cx", xPos)
          .attr("cy", d.yPos)
          .attr("r", dotRadius)
          .attr("fill", colors.dot);
      });
    });

    // Set labels on left with dynamic positioning
    matrixGroup
      .selectAll(".setLabel")
      .data(sets)
      .enter()
      .append("text")
      .attr("x", labelSpace - 10)
      .attr("y", (d) => rowScale(d.name) + rowScale.bandwidth() / 2)
      .attr("text-anchor", "end")
      .attr("alignment-baseline", "middle")
      .attr("font-size", fontSize)
      .attr("font-weight", fontWeight)
      .attr("fill", "#000")
      .text((d) => d.name);

    // ---------- LEFT HORIZONTAL BAR CHART ----------
    const leftBarGroup = g.append("g").attr(
      "transform",
      `translate(-${leftBarWidth + 20}, ${barChartHeight + 60})`
    );

    const xLeft = d3
      .scaleLinear()
      .domain([0, d3.max(sets, (d) => d.size)])
      .range([leftBarWidth, 0]);

    // Add X-axis for left bar chart at the bottom with comma formatting
    const xLeftAxis = d3.axisBottom(xLeft).ticks(2).tickFormat(d3.format(",d"));
    
    leftBarGroup
      .append("g")
      .attr("class", "x-axis-left")
      .attr("transform", `translate(0, ${matrixHeight})`)
      .call(xLeftAxis)
      .selectAll("text")
      .attr("font-size", fontSize)
      .attr("font-weight", fontWeight);

    leftBarGroup
      .selectAll(".leftBar")
      .data(sets)
      .enter()
      .append("rect")
      .attr("x", (d) => xLeft(d.size))
      .attr("y", (d) => rowScale(d.name))
      .attr("width", (d) => leftBarWidth - xLeft(d.size))
      .attr("height", rowScale.bandwidth())
      .attr("fill", colors.leftBar);

    leftBarGroup
      .selectAll(".leftLabel")
      .data(sets)
      .enter()
      .append("text")
      .attr("x", (d) => xLeft(d.size) - 5)
      .attr("y", (d) => rowScale(d.name) + rowScale.bandwidth() / 2)
      .attr("text-anchor", "end")
      .attr("alignment-baseline", "middle")
      .attr("font-size", fontSize)
      .attr("font-weight", fontWeight)
      .attr("fill", "#000")
      .text((d) => d.size.toLocaleString());

  }, [data, dimensions]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: "100%", 
        padding: "20px", 
        backgroundColor: "#fff", 
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <svg ref={svgRef} style={{ display: "block", margin: "0 auto" }}></svg>
    </div>
  );
};

export default UpsetLikePlot;